import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBed } from '@fortawesome/free-solid-svg-icons';
import { buildItineraryGlobeModel } from './globe/itineraryGlobeModel';
import { GlobeCard, GlobeExplore, MODE_ICON, SELECTED, TONE_COLOR } from './SharedGlobeParts';

/**
 * "Itinerary Map" for a shared trip: a web port of the app's ItineraryGlobe (map-itineraries).
 * The model is the app's own (globe/, transpiled from voia-travel-mobile), so both surfaces draw
 * the same numbered stays, day visits, commute legs by mode and the sharer's been-there places.
 *
 * Clicking a stop, day visit or leg badge frames it and opens a detail card. "Explore" on a stop
 * flies to its city and swaps the card for a row of its stay and places, kept in step with the
 * map; the route is hidden there until "Whole trip" flies back out.
 */

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const MAP_STYLE = 'mapbox://styles/talb27/cmilydp6m00rg01qt87xdegcz';
const PRIMARY = '#074E75';
const TEXT_PRIMARY = '#11181C';
const TEXT_SECONDARY = '#687076';

// Room kept clear of the header and the detail card when framing the camera.
const FIT_PADDING = { top: 150, right: 50, bottom: 260, left: 50 };
// Exploring a city keeps the row's height clear instead of the card's.
const EXPLORE_PADDING = { top: 150, right: 40, bottom: 150, left: 40 };

// Semantic zoom, as in the app: the route at globe/country scale; stays and places from city
// scale, photos and names as you get closer.
const DETAIL_ZOOM = 9;
const PHOTO_ZOOM = 11;
const PLACE_LABEL_ZOOM = 13;
const PLACE_ZOOM = 14.5;
const PHOTOS_PER_STOP = 3;

const TONE_EXPRESSION = ['match', ['get', 'tone'], 'loved', TONE_COLOR.loved, 'liked', TONE_COLOR.liked, 'want', TONE_COLOR.want, TONE_COLOR.visited];

// Click priority when features overlap: places over stops over home ends over legs.
const CLICK_LAYERS = [
  'globe-place-cluster', 'globe-place-dot',
  'globe-stop-dot', 'globe-dayvisit-dot',
  'globe-outside-dot',
  'globe-legs-line', 'globe-legs-boat', 'globe-legs-outside',
];
// Hidden while a city is explored.
const ROUTE_LAYERS = [
  'globe-route-line', 'globe-side-trip-line',
  'globe-legs-outside', 'globe-legs-line', 'globe-legs-boat',
  'globe-outside-dot', 'globe-outside-label',
];

const collection = (features) => ({ type: 'FeatureCollection', features });
const point = (id, lng, lat, properties) => ({ type: 'Feature', id, geometry: { type: 'Point', coordinates: [lng, lat] }, properties });

/** Paint that follows the selection and the explored stop; applied on load and on every change. */
function paintFor(selectedId, focusStopId) {
  const dim = focusStopId ? 0.3 : 1;
  const isSelected = ['==', ['get', 'id'], selectedId];
  const isFocus = ['==', ['get', 'id'], focusStopId ?? ''];
  const color = ['case', isSelected, SELECTED, PRIMARY];
  return {
    'globe-legs-outside': { 'line-color': color, 'line-opacity': ['case', isSelected, 0.9, 0.35] },
    'globe-legs-line': { 'line-color': color },
    'globe-legs-boat': { 'line-color': color },
    'globe-dayvisit-dot': { 'circle-radius': ['case', isSelected, 8, 6], 'circle-color': color },
    'globe-stop-dot': {
      'circle-radius': ['case', isSelected, 15, 12],
      'circle-color': color,
      'circle-opacity': ['case', isFocus, 1, dim],
      'circle-stroke-opacity': ['case', isFocus, 1, dim],
    },
    'globe-stop-label': { 'text-opacity': ['case', isFocus, 1, dim] },
    'globe-place-dot': {
      'circle-radius': ['case', isSelected, 9, 6],
      'circle-stroke-color': ['case', isSelected, SELECTED, '#fff'],
      'circle-stroke-width': ['case', isSelected, 3, 2],
    },
  };
}

const halo = { 'text-color': TEXT_PRIMARY, 'text-halo-color': '#fff', 'text-halo-width': 1.5 };

/** The app's layer stack, bottom to top. */
function addLayers(map, shapes) {
  map.addSource('globe-route', { type: 'geojson', data: shapes.route });
  map.addLayer({
    id: 'globe-route-line', type: 'line', source: 'globe-route',
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': PRIMARY, 'line-width': 3, 'line-opacity': 0.8 },
  });
  map.addSource('globe-side-trips', { type: 'geojson', data: shapes.sideTrips });
  map.addLayer({
    id: 'globe-side-trip-line', type: 'line', source: 'globe-side-trips',
    paint: { 'line-color': PRIMARY, 'line-width': 2, 'line-opacity': 0.6, 'line-dasharray': [2, 2] },
  });

  map.addSource('globe-legs', { type: 'geojson', data: shapes.legs });
  // Getting there / heading home: muted, so the trip itself leads.
  map.addLayer({
    id: 'globe-legs-outside', type: 'line', source: 'globe-legs',
    filter: ['!=', ['get', 'role'], 'route'],
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': PRIMARY, 'line-width': 1.5, 'line-opacity': 0.35 },
  });
  map.addLayer({
    id: 'globe-legs-line', type: 'line', source: 'globe-legs',
    filter: ['all', ['==', ['get', 'role'], 'route'], ['!=', ['get', 'mode'], 'boat']],
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': PRIMARY,
      'line-width': ['match', ['get', 'mode'], 'train', 3, 'bus', 2.5, 2],
      'line-opacity': ['match', ['get', 'mode'], 'flight', 0.75, 0.9],
    },
  });
  map.addLayer({
    id: 'globe-legs-boat', type: 'line', source: 'globe-legs',
    filter: ['all', ['==', ['get', 'role'], 'route'], ['==', ['get', 'mode'], 'boat']],
    paint: { 'line-color': PRIMARY, 'line-width': 2.5, 'line-opacity': 0.9, 'line-dasharray': [1.5, 1.5] },
  });

  map.addSource('globe-outside', { type: 'geojson', data: shapes.outside });
  map.addLayer({
    id: 'globe-outside-dot', type: 'circle', source: 'globe-outside',
    paint: {
      'circle-radius': 5, 'circle-color': '#fff', 'circle-stroke-color': PRIMARY,
      'circle-stroke-width': 1.5, 'circle-stroke-opacity': 0.7, 'circle-pitch-alignment': 'map',
    },
  });
  map.addLayer({
    id: 'globe-outside-label', type: 'symbol', source: 'globe-outside',
    layout: {
      'text-field': ['get', 'label'], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 0.8],
      'text-max-width': 10, 'text-optional': true,
    },
    paint: { ...halo, 'text-color': TEXT_SECONDARY },
  });

  map.addSource('globe-points', { type: 'geojson', data: shapes.points });
  map.addLayer({
    id: 'globe-dayvisit-dot', type: 'circle', source: 'globe-points',
    filter: ['==', ['get', 'kind'], 'dayVisit'],
    paint: { 'circle-color': PRIMARY, 'circle-radius': 6, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2, 'circle-pitch-alignment': 'map' },
  });
  map.addLayer({
    id: 'globe-stop-dot', type: 'circle', source: 'globe-points',
    filter: ['==', ['get', 'kind'], 'stop'],
    paint: { 'circle-color': PRIMARY, 'circle-radius': 12, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2.5, 'circle-pitch-alignment': 'map' },
  });
  map.addLayer({
    id: 'globe-stop-number', type: 'symbol', source: 'globe-points',
    filter: ['==', ['get', 'kind'], 'stop'],
    layout: {
      'text-field': ['get', 'number'], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'], 'text-size': 12,
      'text-allow-overlap': true, 'text-ignore-placement': true,
    },
    paint: { 'text-color': '#fff' },
  });
  map.addLayer({
    id: 'globe-stop-label', type: 'symbol', source: 'globe-points',
    filter: ['==', ['get', 'kind'], 'stop'],
    layout: {
      'text-field': ['format', ['get', 'name'], {}, ['get', 'sub'], { 'font-scale': 0.8 }],
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'], 'text-size': 13,
      'text-anchor': 'top', 'text-offset': [0, 1.3], 'text-max-width': 10, 'text-optional': true,
    },
    paint: halo,
  });
  map.addLayer({
    id: 'globe-dayvisit-label', type: 'symbol', source: 'globe-points', minzoom: 5,
    filter: ['==', ['get', 'kind'], 'dayVisit'],
    layout: {
      'text-field': ['get', 'name'], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 0.9],
      'text-max-width': 9, 'text-optional': true,
    },
    paint: halo,
  });

  map.addSource('globe-places', { type: 'geojson', data: shapes.places, cluster: true, clusterRadius: 40, clusterMaxZoom: 15 });
  map.addLayer({
    id: 'globe-place-cluster', type: 'circle', source: 'globe-places', minzoom: DETAIL_ZOOM,
    filter: ['has', 'point_count'],
    paint: {
      'circle-radius': ['step', ['get', 'point_count'], 13, 10, 16], 'circle-color': '#fff',
      'circle-stroke-color': PRIMARY, 'circle-stroke-width': 2, 'circle-pitch-alignment': 'map',
    },
  });
  map.addLayer({
    id: 'globe-place-cluster-count', type: 'symbol', source: 'globe-places', minzoom: DETAIL_ZOOM,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 12, 'text-allow-overlap': true, 'text-ignore-placement': true,
    },
    paint: { 'text-color': PRIMARY },
  });
  map.addLayer({
    id: 'globe-place-dot', type: 'circle', source: 'globe-places', minzoom: DETAIL_ZOOM,
    filter: ['!', ['has', 'point_count']],
    paint: { 'circle-radius': 6, 'circle-color': TONE_EXPRESSION, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2, 'circle-pitch-alignment': 'map' },
  });
  map.addLayer({
    id: 'globe-place-label', type: 'symbol', source: 'globe-places', minzoom: PLACE_LABEL_ZOOM,
    filter: ['!', ['has', 'point_count']],
    layout: {
      'text-field': ['get', 'name'], 'text-size': 11, 'text-anchor': 'top', 'text-offset': [0, 0.9],
      'text-max-width': 9, 'text-optional': true,
    },
    paint: halo,
  });
}

/** A DOM marker whose content React renders (leg badges, beds, photo pins). */
const MapMarker = ({ map, lngLat, anchor = 'center', children }) => {
  const [el] = useState(() => document.createElement('div'));
  const markerRef = useRef(null);
  useEffect(() => {
    const marker = new mapboxgl.Marker({ element: el, anchor }).setLngLat(lngLat).addTo(map);
    markerRef.current = marker;
    return () => marker.remove();
    // Position updates go through the effect below instead of re-creating the marker.
  }, [map, el, anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    markerRef.current?.setLngLat(lngLat);
  }, [lngLat]);
  return createPortal(children, el);
};

const SharedMap = ({ trip, places, owner, onClose }) => {
  const container = useRef(null);
  const [map, setMap] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const model = useMemo(() => buildItineraryGlobeModel(trip, places || []), [trip, places]);
  const ownerName = owner?.username || owner?.firstName || '';

  const [selection, setSelection] = useState(null);
  // The stop being explored: its places on the row, the other stops dimmed and the route hidden
  // until the whole trip is back.
  const [focusStopId, setFocusStopId] = useState(null);
  // Zoom in half steps and the map's rotation, for the detail drawn as DOM markers.
  const [zoom, setZoom] = useState(2);
  const [bearingDeg, setBearingDeg] = useState(0);
  const selectedId = selection?.id ?? '';

  const shapes = useMemo(() => ({
    points: collection([
      ...model.dayVisits.map((d) => point(d.id, d.longitude, d.latitude, { kind: 'dayVisit', id: d.id, name: d.name })),
      ...model.stops.map((s) => point(s.id, s.longitude, s.latitude, {
        kind: 'stop',
        id: s.id,
        name: s.name,
        number: String(s.number),
        // A quiet hint that there's more inside.
        sub: s.placeIds.length ? `\n${s.placeIds.length} place${s.placeIds.length === 1 ? '' : 's'}` : '',
      })),
    ]),
    legs: collection(model.legs.map((l) => ({
      type: 'Feature',
      id: l.id,
      geometry: { type: 'LineString', coordinates: l.coordinates },
      properties: { kind: 'leg', id: l.id, mode: l.mode, role: l.role },
    }))),
    // Far ends of getting-there / heading-home journeys; clicking one selects the leg reaching it.
    outside: collection(model.outsidePlaces.map((o) => point(o.id, o.longitude, o.latitude, {
      kind: 'leg', id: o.legId, label: o.role === 'arrival' ? `From ${o.name}` : `To ${o.name}`,
    }))),
    places: collection(model.places.map((p) => point(p.id, p.longitude, p.latitude, { kind: 'place', id: p.id, name: p.name, tone: p.tone }))),
    route: collection([...model.lines.routeLines, ...model.lines.dayVisitLinesSolid]),
    sideTrips: collection(model.lines.dayVisitLinesDashed),
  }), [model]);

  // A city's best moments as photos: its top loved/liked places with a picture.
  const photoPlaces = useMemo(() => {
    const perStop = new Map();
    return model.places.filter((p) => {
      if (!p.thumbnailUrl || (p.tone !== 'loved' && p.tone !== 'liked')) return false;
      const key = p.stopId ?? '';
      const n = perStop.get(key) ?? 0;
      if (n >= PHOTOS_PER_STOP) return false;
      perStop.set(key, n + 1);
      return true;
    });
  }, [model]);

  const exploreItems = useMemo(() => {
    const stop = model.stops.find((s) => s.id === focusStopId);
    if (!stop) return [];
    return [
      ...model.accommodations.filter((a) => a.stopId === stop.id).map((a) => ({ kind: 'stay', id: a.id, stay: a })),
      ...model.places.filter((p) => p.stopId === stop.id).map((p) => ({ kind: 'place', id: p.id, place: p })),
    ];
  }, [model, focusStopId]);

  useEffect(() => {
    if (!container.current || !mapboxgl.accessToken) return undefined;
    setLoaded(false);
    const first = model.stops[0] ?? model.dayVisits[0];
    const m = new mapboxgl.Map({
      container: container.current,
      style: MAP_STYLE,
      projection: 'globe',
      center: first ? [first.longitude, first.latitude] : [0, 20],
      zoom: 2,
      minZoom: 1,
      maxZoom: 18,
      attributionControl: false,
    });
    m.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
    m.on('zoom', () => {
      const z = Math.round(m.getZoom() * 2) / 2;
      setZoom((prev) => (prev === z ? prev : z));
    });
    m.on('rotate', () => {
      const b = Math.round(m.getBearing());
      setBearingDeg((prev) => (prev === b ? prev : b));
    });
    m.on('load', () => {
      addLayers(m, shapes);
      setLoaded(true);
    });
    setMap(m);
    return () => {
      setMap(null);
      m.remove();
    };
  }, [model, shapes]);

  useEffect(() => {
    if (!map || !loaded) return;
    Object.entries(paintFor(selectedId, focusStopId)).forEach(([layer, props]) => {
      Object.entries(props).forEach(([prop, value]) => map.setPaintProperty(layer, prop, value));
    });
    // Toggled, not removed, so the layers keep their stacking order when the whole trip is back.
    ROUTE_LAYERS.forEach((layer) => map.setLayoutProperty(layer, 'visibility', focusStopId ? 'none' : 'visible'));
  }, [map, loaded, selectedId, focusStopId]);

  const fitAll = useCallback((duration) => {
    if (!map || !model.bounds) return;
    const [[minLng, minLat], [maxLng, maxLat]] = model.bounds;
    // Keep a single-city trip from zooming to street level.
    const padLng = Math.max(0, (1 - (maxLng - minLng)) / 2);
    const padLat = Math.max(0, (1 - (maxLat - minLat)) / 2);
    map.fitBounds([[minLng - padLng, minLat - padLat], [maxLng + padLng, maxLat + padLat]], { padding: FIT_PADDING, duration });
  }, [map, model]);

  useEffect(() => {
    if (loaded) fitAll(1000);
  }, [loaded, fitAll]);

  const flyTo = useCallback((p, zoomLevel, padding) => {
    map?.easeTo({
      center: [p.longitude, p.latitude],
      zoom: zoomLevel,
      padding: { top: padding.top / 2, right: 0, bottom: padding.bottom, left: 0 },
      duration: 700,
    });
  }, [map]);

  // Frame a city: the stop, its stay and its places, never tighter than a few streets.
  const frameCity = useCallback((stopId) => {
    const stop = model.stops.find((s) => s.id === stopId);
    if (!stop || !map) return;
    const pts = [
      stop,
      ...model.accommodations.filter((a) => a.stopId === stopId),
      ...model.places.filter((p) => p.stopId === stopId),
    ];
    const lngs = pts.map((p) => p.longitude);
    const lats = pts.map((p) => p.latitude);
    const pad = 0.015;
    map.fitBounds(
      [[Math.min(...lngs) - pad, Math.min(...lats) - pad], [Math.max(...lngs) + pad, Math.max(...lats) + pad]],
      { padding: EXPLORE_PADDING, duration: 900 },
    );
  }, [map, model]);

  const explore = useCallback((stopId, itemId) => {
    if (!model.stops.some((s) => s.id === stopId)) return;
    setFocusStopId(stopId);
    const stays = model.accommodations.filter((a) => a.stopId === stopId);
    const stopPlaces = model.places.filter((p) => p.stopId === stopId);
    const firstItem = itemId
      ? { kind: stays.some((a) => a.id === itemId) ? 'stay' : 'place', id: itemId }
      : stays[0] ? { kind: 'stay', id: stays[0].id }
        : stopPlaces[0] ? { kind: 'place', id: stopPlaces[0].id } : null;
    setSelection(firstItem);
    if (!itemId) frameCity(stopId); // otherwise the caller frames the item itself
  }, [model, frameCity]);

  const leaveExplore = useCallback(() => {
    setFocusStopId(null);
    setSelection(null);
    fitAll(900);
  }, [fitAll]);

  const select = useCallback((next) => {
    if (next?.kind === 'place' || next?.kind === 'stay') {
      const item = next.kind === 'place'
        ? model.places.find((p) => p.id === next.id)
        : model.accommodations.find((a) => a.id === next.id);
      if (!item) return;
      // Clicking the place you're on steps back out to its city.
      if (next.id === selection?.id && item.stopId && item.stopId === focusStopId) {
        setSelection(null);
        frameCity(item.stopId);
        return;
      }
      if (item.stopId && item.stopId !== focusStopId) explore(item.stopId, item.id);
      setSelection(next);
      flyTo(item, Math.max(map?.getZoom() ?? 0, PLACE_ZOOM), EXPLORE_PADDING);
      return;
    }
    // Anything else on the route leaves the city.
    setFocusStopId(null);
    setSelection(next);
    if (!map || !next) return;
    if (next.kind === 'leg') {
      const leg = model.legs.find((l) => l.id === next.id);
      if (!leg) return;
      map.fitBounds(
        [[Math.min(leg.from.longitude, leg.to.longitude), Math.min(leg.from.latitude, leg.to.latitude)],
          [Math.max(leg.from.longitude, leg.to.longitude), Math.max(leg.from.latitude, leg.to.latitude)]],
        { padding: FIT_PADDING, duration: 800 },
      );
      return;
    }
    const p = next.kind === 'stop'
      ? model.stops.find((s) => s.id === next.id)
      : model.dayVisits.find((d) => d.id === next.id);
    if (!p) return;
    map.easeTo({
      center: [p.longitude, p.latitude],
      padding: { top: 0, right: 0, bottom: FIT_PADDING.bottom / 2, left: 0 },
      duration: 700,
    });
  }, [map, model, focusStopId, selection, explore, frameCity, flyTo]);

  // The map's listeners are bound once; they read the latest handler and focus through refs.
  const selectRef = useRef(select);
  selectRef.current = select;
  const focusRef = useRef(focusStopId);
  focusRef.current = focusStopId;

  // One click handler for every layer, in CLICK_LAYERS priority, with a finger-sized hitbox.
  // Marker clicks stop propagation, so they never reach it.
  useEffect(() => {
    if (!map || !loaded) return undefined;
    const box = (e, r) => [[e.point.x - r, e.point.y - r], [e.point.x + r, e.point.y + r]];
    const onClick = (e) => {
      const hits = map.queryRenderedFeatures(box(e, 14), { layers: CLICK_LAYERS });
      const feature = CLICK_LAYERS.map((id) => hits.find((f) => f.layer.id === id)).find(Boolean);
      if (!feature) {
        // While exploring, the row stays; clicking the map only clears a route card.
        if (!focusRef.current) setSelection(null);
        return;
      }
      const props = feature.properties || {};
      if (props.cluster) {
        // Open a cluster by zooming to where it splits.
        const [lng, lat] = feature.geometry.coordinates;
        map.getSource('globe-places').getClusterExpansionZoom(props.cluster_id, (err, next) => {
          map.easeTo({ center: [lng, lat], zoom: !err && typeof next === 'number' ? next + 0.5 : map.getZoom() + 2, duration: 600 });
        });
        return;
      }
      if (props.id && props.kind) selectRef.current({ kind: props.kind, id: props.id });
    };
    const onMove = (e) => {
      const hit = map.queryRenderedFeatures(box(e, 8), { layers: CLICK_LAYERS }).length > 0;
      map.getCanvas().style.cursor = hit ? 'pointer' : '';
    };
    map.on('click', onClick);
    map.on('mousemove', onMove);
    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMove);
    };
  }, [map, loaded]);

  // Escape steps back: out of a city first, then closes a card.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (focusStopId) leaveExplore();
      else setSelection(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [focusStopId, leaveExplore]);

  const markerClick = (next) => (e) => {
    e.stopPropagation();
    select(next);
  };

  const showDetail = zoom >= DETAIL_ZOOM || !!focusStopId;
  const markersReady = !!map && loaded;

  return (
    <div className="si-map-screen" role="dialog" aria-modal="true" aria-label="Itinerary map">
      <div ref={container} className="si-map" />
      <div className="si-map-gradient" />
      <div className="si-map-header">
        <button type="button" className="si-map-back" onClick={onClose} aria-label="Back">
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="si-map-title-wrap">
          <span className="si-map-title">{trip.name || 'Trip'}</span>
          {ownerName && (
            <span className="si-map-owner">
              <img
                className="si-map-avatar"
                src={owner.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(ownerName)}&background=random&color=fff`}
                alt=""
              />
              {ownerName}
            </span>
          )}
        </div>
        <span className="si-map-header-right" />
      </div>

      {markersReady && showDetail && model.accommodations.map((stay) => (
        <MapMarker key={`stay-${stay.id}`} map={map} lngLat={[stay.longitude, stay.latitude]}>
          <button
            type="button"
            aria-label={stay.name}
            className={`si-globe-stay${stay.id === selectedId ? ' si-globe-badge-selected' : ''}`}
            onClick={markerClick({ kind: 'stay', id: stay.id })}
          >
            <FontAwesomeIcon icon={faBed} />
          </button>
        </MapMarker>
      ))}

      {markersReady && (zoom >= PHOTO_ZOOM || !!focusStopId) && photoPlaces
        .filter((p) => zoom >= PHOTO_ZOOM || p.stopId === focusStopId)
        .map((p) => (
          <MapMarker key={`photo-${p.id}`} map={map} lngLat={[p.longitude, p.latitude]} anchor="bottom">
            <button
              type="button"
              aria-label={p.name}
              className="si-globe-photo"
              style={{ borderColor: p.id === selectedId ? SELECTED : TONE_COLOR[p.tone] }}
              onClick={markerClick({ kind: 'place', id: p.id })}
            >
              <img src={p.thumbnailUrl} alt="" />
            </button>
          </MapMarker>
        ))}

      {markersReady && !focusStopId && model.legs.map((leg) => (
        <MapMarker key={`leg-${leg.id}`} map={map} lngLat={leg.midpoint}>
          <button
            type="button"
            aria-label={`${leg.from.name} to ${leg.to.name}`}
            className={`si-globe-leg${leg.role !== 'route' ? ' si-globe-leg-outside' : ''}${leg.id === selectedId ? ' si-globe-badge-selected' : ''}`}
            onClick={markerClick({ kind: 'leg', id: leg.id })}
          >
            <FontAwesomeIcon
              icon={MODE_ICON[leg.mode]}
              // The plane glyph's nose points east (90°).
              style={leg.mode === 'flight' ? { transform: `rotate(${leg.heading - 90 - bearingDeg}deg)` } : undefined}
            />
          </button>
        </MapMarker>
      ))}

      {focusStopId ? (
        <>
          <button type="button" className="si-globe-whole-trip" onClick={leaveExplore}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Whole trip
          </button>
          <GlobeExplore
            items={exploreItems}
            activeId={selection?.kind === 'place' || selection?.kind === 'stay' ? selection.id : null}
            onActiveChange={(id) => {
              const item = exploreItems.find((it) => it.id === id);
              if (item) select({ kind: item.kind, id });
            }}
          />
        </>
      ) : selection && (
        <GlobeCard
          model={model}
          selection={selection}
          onSelect={select}
          onExplore={(stopId) => explore(stopId)}
          onClose={() => setSelection(null)}
        />
      )}

      {model.stops.length + model.dayVisits.length + model.legs.length === 0 && (
        <div className="si-map-empty">
          <p className="si-map-empty-title">No locations added yet</p>
        </div>
      )}
    </div>
  );
};

export default SharedMap;
